import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Fade,
  IconButton,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, Close, Delete, Edit } from "@material-ui/icons";
import { getHrLeavePublicHolidays } from "../../../store/hub/hrLeave";
import { addSnackbarIssue } from "../../../components/common/StoreCommon";
import {
  apiAddPublicHoliday,
  apiUpdatePublicHoliday,
  apiDeletePublicHoliday,
  getHrLeavePublicHolidays as fetchHrLeavePublicHolidays,
} from "../../../components/common/ApiMiddleware";
import {
  localDateToUtcIso,
  parseDateInputValue,
  toDateInputValue,
  utcToLocalDate,
} from "../utils/holidayDates";

function formatLocalDate(utcValue) {
  const localDate = utcToLocalDate(utcValue);
  if (!localDate) return "";
  return localDate.toLocaleDateString();
}

export default function PublicHolidaysDialog({ open, onClose }) {
  const classes = useStyles();
  const publicHolidays = useSelector(getHrLeavePublicHolidays);

  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const manageRange = useMemo(() => {
    const year = new Date().getFullYear();
    const fromLocal = new Date(year - 1, 0, 1);
    const toLocal = new Date(year + 2, 11, 31);
    return {
      fromUtcIso: localDateToUtcIso(fromLocal),
      toUtcIso: new Date(
        toLocal.getFullYear(),
        toLocal.getMonth(),
        toLocal.getDate(),
        23,
        59,
        59,
        999
      ).toISOString(),
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchHrLeavePublicHolidays(manageRange.fromUtcIso, manageRange.toUtcIso).catch(
      () => {
        addSnackbarIssue("Unable to load public holidays", "warning");
      }
    );
  }, [open, manageRange.fromUtcIso, manageRange.toUtcIso]);

  const sortedHolidays = useMemo(() => {
    return [...(publicHolidays || [])].sort((a, b) => {
      return (
        new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
      );
    });
  }, [publicHolidays]);

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setStartDate(null);
    setEndDate(null);
    setFormError("");
    setFormOpen(false);
  };

  const openAdd = () => {
    setEditingId(null);
    setTitle("");
    setStartDate(null);
    setEndDate(null);
    setFormError("");
    setFormOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setTitle(record.title || "");
    setStartDate(utcToLocalDate(record.startDateTime));
    setEndDate(utcToLocalDate(record.endDateTime));
    setFormError("");
    setFormOpen(true);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validate = () => {
    if (!title.trim()) {
      setFormError("Please enter a title");
      return false;
    }
    if (!startDate) {
      setFormError("Please enter a start date");
      return false;
    }
    if (!endDate) {
      setFormError("Please enter an end date");
      return false;
    }
    if (endDate < startDate) {
      setFormError("End date must be on or after the start date");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setFormError("");
    try {
      const payload = {
        title: title.trim(),
        startDateTime: localDateToUtcIso(startDate),
        endDateTime: localDateToUtcIso(endDate),
      };
      if (editingId != null) {
        await apiUpdatePublicHoliday({ publicHolidayId: editingId, ...payload });
        addSnackbarIssue("Public holiday updated", "success", false, 5000);
      } else {
        await apiAddPublicHoliday(payload);
        addSnackbarIssue("Public holiday added", "success", false, 5000);
      }
      await fetchHrLeavePublicHolidays(
        manageRange.fromUtcIso,
        manageRange.toUtcIso
      );
      resetForm();
    } catch (e) {
      setFormError("Unable to save public holiday");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete public holiday "${record.title}"?`)) return;
    try {
      await apiDeletePublicHoliday(record.id);
      addSnackbarIssue("Public holiday removed", "success", false, 5000);
      await fetchHrLeavePublicHolidays(
        manageRange.fromUtcIso,
        manageRange.toUtcIso
      );
      if (editingId === record.id) resetForm();
    } catch (e) {
      addSnackbarIssue("Unable to delete public holiday", "warning");
    }
  };

  const dateInputStyle = {
    width: "100%",
    height: 40,
    padding: "8px 12px",
    fontSize: 14,
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: 4,
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <Modal open={open} onClose={handleClose} className={classes.modal}>
      <Fade in={open}>
        <Paper className={classes.paper}>
          <Box className={classes.header}>
            <Typography variant="h6">Public Holidays</Typography>
            <IconButton size="small" onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>

          <Box className={classes.toolbar}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={openAdd}
            >
              Add
            </Button>
          </Box>

          {formOpen ? (
            <Box className={classes.form}>
              <TextField
                label="Title"
                variant="outlined"
                fullWidth
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setFormError("");
                }}
              />
              <Box>
                <Typography variant="caption" className={classes.dateLabel}>
                  Start date
                </Typography>
                <input
                  type="date"
                  value={toDateInputValue(startDate)}
                  onChange={(e) => {
                    const next = parseDateInputValue(e.target.value);
                    setStartDate(next);
                    if (next && !endDate) setEndDate(next);
                    setFormError("");
                  }}
                  style={dateInputStyle}
                />
              </Box>
              <Box>
                <Typography variant="caption" className={classes.dateLabel}>
                  End date
                </Typography>
                <input
                  type="date"
                  value={toDateInputValue(endDate)}
                  min={toDateInputValue(startDate) || undefined}
                  onChange={(e) => {
                    setEndDate(parseDateInputValue(e.target.value));
                    setFormError("");
                  }}
                  style={dateInputStyle}
                />
              </Box>
              {formError ? (
                <Typography variant="caption" className={classes.error}>
                  {formError}
                </Typography>
              ) : null}
              <Box className={classes.formActions}>
                <Button onClick={resetForm} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  Save
                </Button>
              </Box>
            </Box>
          ) : null}

          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Title</TableCell>
                  <TableCell className={classes.headerCell}>Start</TableCell>
                  <TableCell className={classes.headerCell}>End</TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedHolidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="textSecondary">
                        No public holidays
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedHolidays.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>{record.title}</TableCell>
                      <TableCell>
                        {formatLocalDate(record.startDateTime)}
                      </TableCell>
                      <TableCell>
                        {formatLocalDate(record.endDateTime)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => openEdit(record)}
                          aria-label="Edit"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(record)}
                          aria-label="Delete"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Fade>
    </Modal>
  );
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    width: "90%",
    maxWidth: 720,
    maxHeight: "85vh",
    overflow: "auto",
    padding: theme.spacing(2),
    outline: "none",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
  dateLabel: {
    display: "block",
    marginBottom: 4,
    color: "rgb(100, 100, 100)",
  },
  formActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  error: {
    color: theme.palette.error.main,
  },
  tableContainer: {
    maxHeight: 360,
  },
  headerCell: {
    fontWeight: 700,
  },
}));
